import React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Form, FormControl, FormField, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

const passwordSchema = z.object({
  currentPassword: z.string().min(1, "Le mot de passe actuel est requis"),
  newPassword: z.string()
    .min(8, "Le mot de passe doit contenir au moins 8 caractères")
    .regex(/[A-Z]/, "Le mot de passe doit contenir au moins une majuscule")
    .regex(/[0-9]/, "Le mot de passe doit contenir au moins un chiffre")
    .regex(/[^A-Za-z0-9]/, "Le mot de passe doit contenir au moins un caractère spécial"),
  confirmPassword: z.string(),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: "Les mots de passe ne correspondent pas",
  path: ["confirmPassword"],
});

type PasswordFormData = z.infer<typeof passwordSchema>;

export function PasswordSettings() {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = React.useState(false);

  const form = useForm<PasswordFormData>({
    resolver: zodResolver(passwordSchema),
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
  });



  const onSubmit = async (data: PasswordFormData) => {
    setIsLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({
        password: data.newPassword
      });

      if (error) {
        toast({
          title: "Erreur",
          description: error.message === "New password should be different from the old password."
            ? "Le nouveau mot de passe doit être différent de l'ancien."
            : "Impossible de mettre à jour le mot de passe. Veuillez réessayer.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Succès",
          description: "Votre mot de passe a été mis à jour avec succès.",
        });
        form.reset();
      }
    } catch (_error) {
      toast({
        title: "Erreur",
        description: "Une erreur inattendue s'est produite.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full flex flex-col gap-5">
      {/* Header */}
      <div className="flex flex-col gap-1">
        <h2 className="text-gray-800 text-2xl font-medium font-['Bricolage_Grotesque']">
          Changement de mot de passe
        </h2>
        <p className="text-neutral-400 text-sm font-medium font-['Bricolage_Grotesque']">
          Mettez à jour votre mot de passe pour sécuriser votre compte
        </p>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="w-full space-y-5">
          {/* Mot de passe actuel */}
          <FormField
            control={form.control}
            name="currentPassword"
            render={({ field }) => (
              <div className="w-full flex flex-col gap-2">
                <FormLabel className="text-neutral-400 text-sm font-medium font-['Bricolage_Grotesque']">
                  Mot de passe actuel
                </FormLabel>
                <FormControl>
                  <Input 
                    type="password" 
                    placeholder="Votre mot de passe actuel"
                    {...field} 
                    className="w-full px-3 py-4 h-12 bg-white rounded-lg border border-gray-300 text-gray-800 text-sm font-medium font-['Bricolage_Grotesque'] focus:border-teal-400 focus:ring-1 focus:ring-teal-400 focus:outline-none"
                  />
                </FormControl>
                <FormMessage />
              </div>
            )}
          />

          {/* Nouveau mot de passe */}
          <FormField
            control={form.control}
            name="newPassword"
            render={({ field }) => (
              <div className="w-full flex flex-col gap-2">
                <FormLabel className="text-neutral-400 text-sm font-medium font-['Bricolage_Grotesque']">
                  Nouveau mot de passe
                </FormLabel>
                <FormControl>
                  <Input 
                    type="password" 
                    placeholder="Votre nouveau mot de passe"
                    {...field} 
                    className="w-full px-3 py-4 h-12 bg-white rounded-lg border border-gray-300 text-gray-800 text-sm font-medium font-['Bricolage_Grotesque'] focus:border-teal-400 focus:ring-1 focus:ring-teal-400 focus:outline-none"
                  />
                </FormControl>
                <FormMessage />
              </div>
            )}
          />

          {/* Confirmer nouveau mot de passe */}
          <FormField
            control={form.control}
            name="confirmPassword"
            render={({ field }) => (
              <div className="w-full flex flex-col gap-2">
                <FormLabel className="text-neutral-400 text-sm font-medium font-['Bricolage_Grotesque']">
                  Confirmer le nouveau mot de passe
                </FormLabel>
                <FormControl>
                  <Input 
                    type="password" 
                    placeholder="Confirmez votre nouveau mot de passe"
                    {...field} 
                    className="w-full px-3 py-4 h-12 bg-white rounded-lg border border-gray-300 text-gray-800 text-sm font-medium font-['Bricolage_Grotesque'] focus:border-teal-400 focus:ring-1 focus:ring-teal-400 focus:outline-none"
                  />
                </FormControl>
                <FormMessage />
              </div>
            )}
          />

          {/* Save button */}
          <div 
            className="h-10 p-3 bg-teal-400 rounded-lg inline-flex justify-start items-center gap-1 overflow-hidden cursor-pointer"
            onClick={form.handleSubmit(onSubmit)}
          >
            <div className="justify-center text-white text-sm font-semibold font-['Bricolage_Grotesque']">
              {isLoading ? "Mise à jour..." : "Mettre à jour le mot de passe"}
            </div>
          </div>
        </form>
      </Form>
    </div>
  );
}